import { useId, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, X } from "lucide-react";
import { useCreateUser, useUpdateUser } from "@/api/hooks";
import { ApiClientError } from "@/api/client";
import type { Location, User, UserRole } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ASSIGNABLE_ROLES,
  PASSWORD_MIN_LENGTH,
  ROLE_DESCRIPTIONS,
  validateUser,
  type UserErrors,
} from "../../userForm";
import { SwitchField, TextField } from "../loan-rules/fields";

const NO_LOCATION = "";

/**
 * Create a staff account, or edit an existing one. Only a super admin may
 * choose roles; the server enforces it too. Mounted only while open, so each
 * opening starts from the user being edited.
 */
export function UserDialog({
  user,
  locations,
  canManageRoles,
  isSelf,
  onClose,
  onSaved,
}: {
  /** null creates a new account */
  user: User | null;
  locations: Location[];
  canManageRoles: boolean;
  /** One's own account cannot be deactivated (the server refuses it too) */
  isSelf: boolean;
  onClose: () => void;
  onSaved: (user: User) => void;
}) {
  const isNew = user === null;
  const formId = useId();

  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [password, setPassword] = useState("");
  const [locationId, setLocationId] = useState(user?.locationId ?? NO_LOCATION);
  // Kept as stored, so roles that cannot be granted here (super admin) survive an edit
  const [roles, setRoles] = useState<UserRole[]>(
    user?.roles.filter((role) => role !== "ROLE_USER") ?? []
  );
  const [active, setActive] = useState(user?.active ?? true);
  const [errors, setErrors] = useState<UserErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const isPending = createUser.isPending || updateUser.isPending;

  const sortedLocations = [...locations].sort((a, b) => a.name.localeCompare(b.name, "fr"));
  // The API cannot detach a user from their location once set
  const canHaveNoLocation = isNew || user.locationId === null;

  const toggleRole = (role: UserRole, checked: boolean) => {
    setRoles((current) => (checked ? [...current, role] : current.filter((r) => r !== role)));
  };

  const field = (key: keyof UserErrors, setter: (value: string) => void) => (value: string) => {
    setter(value);
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = async () => {
    setServerError(null);
    const nextErrors = validateUser({ firstName, lastName, email, password }, isNew);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      ...(password !== "" && { password }),
      ...(locationId !== NO_LOCATION && { locationId }),
      ...(canManageRoles && { roles }),
      active,
    };

    try {
      const saved = isNew
        ? await createUser.mutateAsync({ ...input, password })
        : await updateUser.mutateAsync({ id: user.id, ...input });
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setErrors({ email: "Un compte utilise déjà cette adresse e-mail." });
        return;
      }
      if (err instanceof ApiClientError && err.status === 403) {
        setServerError("Vous n'avez pas les droits pour cette modification.");
        return;
      }
      const violations = err instanceof ApiClientError ? (err.data?.violations ?? []) : [];
      const fieldErrors: UserErrors = {};
      for (const violation of violations) {
        const path = violation.propertyPath;
        if (
          path === "firstName" ||
          path === "lastName" ||
          path === "email" ||
          path === "password"
        ) {
          fieldErrors[path] = violation.message;
        }
      }
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else setServerError(err instanceof Error ? err.message : "L'enregistrement a échoué");
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-gray-950/40 backdrop-blur-sm" />
        <Dialog.Content className="animate-fade-in fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-gray-900">
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                {isNew ? "Nouvel utilisateur" : `Modifier ${user.fullName}`}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {isNew
                  ? "Communiquez le mot de passe à la personne : elle s'en servira pour se connecter."
                  : "Laissez le mot de passe vide pour conserver l'actuel."}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Fermer">
                <X aria-hidden />
              </Button>
            </Dialog.Close>
          </header>

          <form
            id={formId}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
            className="min-h-0 flex-1 space-y-8 overflow-y-auto px-6 py-6"
          >
            <section aria-labelledby={`${formId}-identity`} className="space-y-4">
              <SectionTitle id={`${formId}-identity`}>Identité</SectionTitle>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label="Prénom"
                  value={firstName}
                  onChange={field("firstName", setFirstName)}
                  error={errors.firstName}
                  maxLength={255}
                  autoComplete="off"
                  autoFocus
                />
                <TextField
                  label="Nom"
                  value={lastName}
                  onChange={field("lastName", setLastName)}
                  error={errors.lastName}
                  maxLength={255}
                  autoComplete="off"
                />
              </div>
              <TextField
                label="E-mail"
                type="email"
                value={email}
                onChange={field("email", setEmail)}
                error={errors.email}
                hint="Sert d'identifiant de connexion."
                maxLength={255}
                autoComplete="off"
              />
              <TextField
                label={isNew ? "Mot de passe" : "Nouveau mot de passe"}
                type="password"
                value={password}
                onChange={field("password", setPassword)}
                error={errors.password}
                hint={`Au moins ${String(PASSWORD_MIN_LENGTH)} caractères.`}
                maxLength={255}
                autoComplete="new-password"
              />
            </section>

            <section aria-labelledby={`${formId}-access`} className="space-y-4">
              <SectionTitle id={`${formId}-access`}>Accès</SectionTitle>
              <div>
                <label
                  htmlFor={`${formId}-location`}
                  className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200"
                >
                  Établissement
                </label>
                <select
                  id={`${formId}-location`}
                  value={locationId}
                  onChange={(event) => {
                    setLocationId(event.target.value);
                  }}
                  aria-describedby={`${formId}-location-hint`}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  {canHaveNoLocation && <option value={NO_LOCATION}>Aucun</option>}
                  {sortedLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                <p
                  id={`${formId}-location-hint`}
                  className="mt-1 text-xs text-gray-500 dark:text-gray-400"
                >
                  Le compte a accès à cet établissement et à ceux qui lui sont rattachés.
                </p>
              </div>

              {canManageRoles ? (
                <fieldset>
                  <legend className="mb-1.5 text-sm text-gray-700 dark:text-gray-200">Rôles</legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {ASSIGNABLE_ROLES.map((role) => {
                      const checked = roles.includes(role);
                      return (
                        <label
                          key={role}
                          className={cn(
                            "has-focus-visible:ring-ring cursor-pointer rounded-xl border p-4 transition-colors has-focus-visible:ring-2",
                            checked
                              ? "border-primary/50 bg-primary/5 dark:bg-primary/10"
                              : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                          )}
                        >
                          <span className="flex items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                toggleRole(role, event.target.checked);
                              }}
                              className="accent-primary h-4 w-4"
                            />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {ROLE_DESCRIPTIONS[role].label}
                            </span>
                          </span>
                          <span className="mt-1 block pl-6.5 text-xs text-gray-500 dark:text-gray-400">
                            {ROLE_DESCRIPTIONS[role].description}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Sans rôle, le compte peut se connecter et consulter le catalogue.
                  </p>
                </fieldset>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Seul un super administrateur peut attribuer des rôles.
                </p>
              )}

              {!isNew && !isSelf && (
                <SwitchField
                  label="Compte actif"
                  description="Désactivé, le compte ne peut plus se connecter ; son historique est conservé."
                  checked={active}
                  onChange={setActive}
                />
              )}
            </section>
          </form>

          <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {serverError && (
              <p role="alert" className="mr-auto text-sm text-red-700 dark:text-red-400">
                {serverError}
              </p>
            )}
            <Dialog.Close asChild>
              <Button variant="ghost">Annuler</Button>
            </Dialog.Close>
            <Button type="submit" form={formId} disabled={isPending}>
              {isPending && <Loader2 className="animate-spin" aria-hidden />}
              {isNew ? "Créer le compte" : "Enregistrer"}
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SectionTitle({ id, children }: { id: string; children: string }) {
  return (
    <h3 id={id} className="text-sm font-semibold text-gray-900 dark:text-white">
      {children}
    </h3>
  );
}
